import { Right } from '@/features/invoices/components/List/Table/Uploader/components/Right';
import {
  RECEIVED_INVOICE_FILE_ERROR,
  TReceivedInvoiceUpload,
  TUploadFile,
} from '@/features/invoices/components/List/Table/Uploader/type';

import { InlineNotifications } from '@/features/invoices/components/List/Table/Uploader/components/InlineNotifications';
import {
  ALLOWED_FILE_TYPES,
  FILE_TYPE_UPLOAD_ALLOWED,
  UPLOAD_FILE_LIMIT_TOTAL,
  UPLOAD_LIMIT_SIZE_PER_FILE,
} from '@/features/invoices/components/List/Table/Uploader/constant';
import { useFileOptions } from '@/features/invoices/components/List/Table/Uploader/hooks/useFileOptions';
import {
  useFilesValidation,
  useFilesValidationStatus,
  useValidationFiles,
} from '@/features/invoices/components/List/Table/Uploader/hooks/useFileUpload';
import { useSubmitBulkManualUpload } from '@/features/invoices/components/List/Table/Uploader/hooks/useSubmitBulkManualUpload';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  Form,
  InlineNotification,
  Loading,
  UploadDrawerLeadingFiles,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  callRefetch: VoidFunction;
};

export const RECEIVED_INVOICE_DEFAULT: TReceivedInvoiceUpload = {
  files: [],
  receipt_type: 'paper',
  ap_payee_id: '',
  pic_ids: [],
};

export const Uploader: FC<Props> = memo(({ open, setOpen, callRefetch }) => {
  const { t } = useTranslation();
  const [headerErrorMsg, setHeaderErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const notify = useToastAPI();
  const methods = useForm<TReceivedInvoiceUpload>({
    defaultValues: RECEIVED_INVOICE_DEFAULT,
    mode: 'onChange',
  });
  const {
    reset,
    control,
    getValues,
    watch,
    setError,
    formState: { isValid },
  } = methods;

  const files = watch('files');
  const { filesWithError } = useFilesValidationStatus(files);
  const validationErrors = useValidationFiles();
  const { replace, update } = useFieldArray({ control, name: 'files' });
  const setFiles = useCallback(
    (files: TUploadFile[]) => {
      replace(files);
    },
    [replace]
  );
  const { maxFileCountVisible, onMaxFileCountHidden, totalMaxSizeErrVisible } =
    useFilesValidation(files, setFiles);

  const handleOptionChecked = useCallback(
    (checked: boolean, name: string, index?: number) => {
      if (index === undefined) return;
      const file = files[index];
      if (!file) return;
      file.fileOption = {
        ...(file.fileOption || {}),
        [name]: checked,
      };
      update(index, file);
    },
    [files, update]
  );

  const handleCheckedAll = useCallback(
    (checked: boolean, name: string) => {
      replace(
        files.map((file) => {
          const option = 'fileOption' in file ? file.fileOption || {} : {};
          option[name] = checked;
          file.fileOption = option;
          return file;
        })
      );
    },
    [files, replace]
  );

  const options = useFileOptions(handleOptionChecked);

  const onClose = useCallback(() => {
    setOpen(false);
    setHeaderErrorMsg('');
    reset(RECEIVED_INVOICE_DEFAULT);
  }, [reset, setOpen]);

  const onChange = useCallback(
    (file: TUploadFile | TUploadFile[]) => {
      const files = Array.isArray(file) ? file : [file];
      options.forEach((option) => {
        files.forEach((file) => {
          file.fileOption = {
            ...(file.fileOption || {}),
            [option.name]: true,
          };

          const { inValid, message } = validationErrors(file);
          if (inValid) {
            file.errorMessage = message;
            file.error = RECEIVED_INVOICE_FILE_ERROR.MAX_SIZE;
          }
        });
      });
    },
    [options, validationErrors]
  );

  const uploader = useMemo(
    () => ({
      append: true,
      description: (
        <>
          <p>
            {t('max_file_upload_and_maximum_size', {
              max_file: UPLOAD_FILE_LIMIT_TOTAL,
              max_size: UPLOAD_LIMIT_SIZE_PER_FILE,
            })}
          </p>
          <p>
            {t('file_type_upload_allowed', {
              file_types: FILE_TYPE_UPLOAD_ALLOWED,
            })}
          </p>
        </>
      ),
      name: 'files' as const,
      control: control,
      accept: ALLOWED_FILE_TYPES,
      testId: 'received-invoice',
      onChange,
    }),
    [t, control, onChange]
  );

  const setErrorMessage = useCallback(
    (errorMsg: string, index: number, error: RECEIVED_INVOICE_FILE_ERROR) => {
      if (index === undefined) return;
      const file = files[index];
      if (!file) return;
      file.errorMessage = errorMsg;
      file.error = error;
      update(index, file);
    },
    [files, update]
  );

  const { onSubmit } = useSubmitBulkManualUpload({
    setHeaderErrorMsg,
    setFileErrorMsg: setErrorMessage,
    setError,
    setIsLoading,
    notify,
    replace,
    reset,
    callRefetch,
  });

  return (
    <div className={cx(styles['container'])}>
      <Form<TReceivedInvoiceUpload> formMethod={methods} onSubmit={onSubmit}>
        <UploadDrawerLeadingFiles
          key={open ? 'open' : 'close'}
          onClose={onClose}
          title={t('received_invoice.uploader.title')}
          header={
            <InlineNotification
              message={headerErrorMsg}
              type='error'
              visible={Boolean(headerErrorMsg)}
              onClick={() => setHeaderErrorMsg('')}
            />
          }
          open={open}
          size='wide'
          control={control}
          name='files'
          arrayName='files'
          uploader={uploader}
          options={options}
          isShowCheckAll
          onCheckAll={handleCheckedAll}
          isDisabledCheckAll={files.length === 0}
          details={
            <>
              {isLoading && <Loading isOverlay size='xlarge' color='default' />}
              <Right control={control} getValues={getValues} reset={reset} />
            </>
          }
          uploadListHeader={
            <InlineNotifications
              totalMaxSizeErrVisible={totalMaxSizeErrVisible}
              maxFileCountVisible={maxFileCountVisible}
              onMaxFileCountHidden={onMaxFileCountHidden}
              filesWithError={filesWithError}
            />
          }
          footer={
            <div className={cx(styles['footer'])}>
              <ButtonV2
                color='primary'
                type='submit'
                testId='submit-button'
                name='upload'
                size='sm'
                isDisabled={
                  totalMaxSizeErrVisible ||
                  filesWithError.length > 0 ||
                  files.length === 0 ||
                  !isValid
                }
              >
                {t('upload')}
              </ButtonV2>
            </div>
          }
        />
      </Form>
    </div>
  );
});
Uploader.displayName = 'Uploader';
